import { SnakeCasedProperties } from "type-fest"
import {
  GroupStatus,
  NoteFragment,
  NoteVisibility,
  useNotePermissionsQuery
} from "@pathwright/graphql-client/src/generated"
import useCohortPermissions, {
  CohortPermissions,
  useCohortPermissionsFragment
} from "../cohort/useCohortPermissions"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { ContextInput } from "./useNotes"

export type NotePermissions = {
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  canViewFullAuthorName: boolean
  visibilityOptions: Set<NoteVisibility>
} & CohortPermissions

// Optionally provide note for scoping permissions to specific note.
function useNotePermissions(
  context: SnakeCasedProperties<ContextInput>,
  note?: NoteFragment | null
): NotePermissions {
  const { me } = usePathwrightContext()

  // Assess if the note is local to the cohort context (if not, it originates
  // from the source cohort).
  const noteIsLocalToCohort = note?.cohort_id === note?.created_from_cohort_id

  // TODO: after upgrading to Apollo Client v4, use TS type safe skipToken: https://www.apollographql.com/docs/react/data/queries#skipping-queries-with-skiptoken
  // Do this across the codebase.
  const notePermissionsQuery = useNotePermissionsQuery({
    variables: { cohortId: context.cohort_id! },
    skip: !note || noteIsLocalToCohort
  })

  const cohort = useCohortPermissionsFragment(context.cohort_id!)

  // Check if the cohort has a resource license.
  const noteIsLicensed =
    !noteIsLocalToCohort &&
    !!notePermissionsQuery.data?.cohort?.resource_license
  // Check if the cohort is complete.
  const cohortIsComplete = cohort?.status === GroupStatus.Completed
  // Check if user registration is soft-archived.
  const userRegistrationIsSoftArchived =
    !!cohort?.user_registration?.softArchivedTime

  const cohortPerms = useCohortPermissions({
    cohortId: context.cohort_id
  })

  // Define narrowed base permissions that may be broadened below based on user permissions.
  const perms: NotePermissions = {
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    canViewFullAuthorName: false,
    visibilityOptions: new Set(),
    ...cohortPerms
  }

  // Authors can update/delete their own notes.
  // This is likely too permissive, but reflects legacy behavior.
  if (note?.user?.id === me?.id) {
    perms.canUpdate = true
    perms.canDelete = true
  }

  // Learners+ can create author-only notes.
  if (cohortPerms.hasLearnerLevelAccess) {
    perms.canCreate = true
    perms.visibilityOptions.add(NoteVisibility.Author)
  }

  // Moderators+ can set staff/everyone visibility.
  if (cohortPerms.hasModeratorLevelAccess) {
    perms.canViewFullAuthorName = true
    perms.visibilityOptions.add(NoteVisibility.Staff)
    perms.visibilityOptions.add(NoteVisibility.Everyone)

    // They can also update/delete notes local to the cohort.
    if (noteIsLocalToCohort) {
      perms.canUpdate = true
      perms.canDelete = true
    }
  }

  // Editors+ can update/delete any note.
  if (cohortPerms.hasEditorLevelAccess) {
    perms.canUpdate = true
    perms.canDelete = true
  } else {
    // When user does not have editor access, they may be restricted to the
    // base permissions depending on cohort and registration status.
    if (cohortIsComplete || userRegistrationIsSoftArchived) {
      perms.canCreate = false
      perms.canUpdate = false
      perms.canDelete = false
    }
  }

  // When non-local note is licensed, restrict modifications (user cannot update or delete).
  // This reflects legacy behavior.
  if (!noteIsLocalToCohort && noteIsLicensed) {
    perms.canUpdate = false
    perms.canDelete = false
  }

  return perms
}

export default useNotePermissions
